// src/services/activityFeedService.js
// D-297 Team Activity v1 — the live reads behind the Owner's Team Activity page.
//
// WHY THIS EXISTS: the page needs ~18 company-scoped listeners (one per
// collection + time field) plus the company's users. Which collections, which
// fields and which filters is decided ONLY by activityQueries() in
// src/utils/activityMap.js (pure + Jest); this file just turns those plain specs
// into Firestore onSnapshot queries and hands the raw docs back. It never
// writes, never decides who did what, never colours anything.
//
// Every query starts with where("company_id","==",companyId), so the read rules
// (sameCompany / poViewRoles / canEnterBills — all include owner) can prove it.
// The indexes are in firestore.indexes.json (commit 3, 4 Nov).

// v1.1 (Home card): loadActivityOnce() runs the SAME specs once with getDocs
// (no listeners), and loadTeamActivityCounts() turns them into the card's
// counts. The counts are kept 30 minutes in sessionStorage (option B, Ravi
// 4 Nov) — readCountsCache() / writeCountsCache(); every storage access is in
// try/catch (blocked storage = the card simply reads again).

import { collection, getDocs, limit, onSnapshot, orderBy, query, Timestamp, where } from "firebase/firestore";
import { db } from "../firebase";
import {
  CACHE_KEY,
  activityQueries, buildActivityRows, cacheEntry, collectActions, isCacheFresh, limitHit, readCacheEntry,
  scoreCounts, windowStart,
} from "../utils/activityMap";
import { retryOnPermissionDenied } from "../utils/retryOnPermissionDenied";

const buildQuery = (spec) => query(
  collection(db, spec.collection),
  ...spec.filters.map((f) => where(f.field, f.op, f.value)),
  orderBy(spec.orderBy.field, spec.orderBy.direction),
  limit(spec.limit)
);

/**
 * Opens one listener per activity source.
 * @param {Object}   a
 * @param {string}   a.companyId
 * @param {number}   a.sinceMs                    windowStart(now)
 * @param {Function} a.onSource(sourceId, docs, size)  docs = [{ id, data }]
 * @param {Function} a.onError(sourceId, error)
 * @returns {Function} closes every listener
 */
export const subscribeActivity = ({ companyId, sinceMs, onSource, onError }) => {
  const specs = activityQueries(companyId, Timestamp.fromMillis(sinceMs));
  const unsubscribes = specs.map((spec) => onSnapshot(
    buildQuery(spec),
    (snap) => onSource(spec.sourceId, snap.docs.map((d) => ({ id: d.id, data: d.data() })), snap.size),
    (err) => onError(spec.sourceId, err)
  ));
  return () => unsubscribes.forEach((u) => u());
};

/**
 * The same sources, read ONCE (getDocs). A failed source is reported in
 * `errors`, never dropped. Home runs right after login, so each read retries
 * a permission-denied a few times (claims race).
 * @returns {Promise<{ docsBySource, sizes, errors }>}
 */
export const loadActivityOnce = async ({ companyId, sinceMs }) => {
  const specs = activityQueries(companyId, Timestamp.fromMillis(sinceMs));
  const settled = await Promise.allSettled(
    specs.map((spec) => retryOnPermissionDenied(() => getDocs(buildQuery(spec))))
  );
  const docsBySource = {};
  const sizes = {};
  const errors = {};
  settled.forEach((r, i) => {
    const id = specs[i].sourceId;
    if (r.status === "fulfilled") {
      docsBySource[id] = r.value.docs.map((d) => ({ id: d.id, data: d.data() }));
      sizes[id] = r.value.size;
    } else {
      errors[id] = r.reason;
    }
  });
  return { docsBySource, sizes, errors };
};

/** The company's users, read once (same shape as subscribeCompanyUsers). */
export const loadCompanyUsersOnce = async (companyId) => {
  const company = String(companyId || "").trim();
  if (!company) throw new Error("company_id is required to read users");
  const snap = await retryOnPermissionDenied(() => getDocs(query(collection(db, "users"), where("company_id", "==", company))));
  return snap.docs.map((d) => ({ ...d.data(), docId: d.id }));
};

/**
 * The Home card's counts, read once — scoreCounts(): green / amber / red /
 * neverLoggedIn WITHOUT the viewer (the Owner looking at Home, commit 7).
 * complete = the claim was there, the users and EVERY source were read and no
 * source hit its limit. Only a complete result may be shown as a number.
 * @returns {Promise<{ complete: boolean, counts: object|null, failed: string[] }>}
 */
export const loadTeamActivityCounts = async ({ companyId, nowMs, viewer }) => {
  const ok = await waitForCompanyClaim(companyId);
  if (!ok) return { complete: false, counts: null, failed: ["login"] };
  const sinceMs = windowStart(nowMs);
  const [users, activity] = await Promise.all([
    loadCompanyUsersOnce(companyId).catch((err) => { console.error("[TeamActivity card] users:", err); return null; }),
    loadActivityOnce({ companyId, sinceMs }),
  ]);
  const failed = [
    ...(users ? [] : ["users"]),
    ...Object.keys(activity.errors),
    ...Object.entries(activity.sizes).filter(([, n]) => limitHit(n)).map(([id]) => `${id} (limit)`),
  ];
  Object.entries(activity.errors).forEach(([id, err]) => console.error(`[TeamActivity card] ${id}:`, err));
  if (!users) return { complete: false, counts: null, failed };
  const result = buildActivityRows({ users, actions: collectActions(activity.docsBySource, sinceMs), nowMs });
  return { complete: failed.length === 0, counts: scoreCounts(result, viewer), failed };
};

/** Fresh cached score counts for this company + viewer, or null. Never throws. */
export const readCountsCache = (companyId, nowMs, viewer) => {
  try {
    const entry = readCacheEntry(window.sessionStorage.getItem(CACHE_KEY));
    return isCacheFresh(entry, nowMs, companyId, viewer) ? entry.counts : null;
  } catch (err) {
    return null;
  }
};

/** Keeps complete score counts (scoreCounts()) for 30 minutes. Never throws (blocked storage = no cache). */
export const writeCountsCache = (companyId, viewer, nowMs, counts) => {
  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(cacheEntry({ companyId, viewer, nowMs, counts })));
  } catch (err) {
    // storage blocked / full — the card just reads again next time
  }
};

/**
 * The company's users, live. docId = the users doc id (≠ Auth uid for
 * technicians); it is set AFTER the doc's own fields so a stray "docId" field
 * in a doc can never replace it.
 */
export const subscribeCompanyUsers = ({ companyId, onUsers, onError }) => {
  const company = String(companyId || "").trim();
  if (!company) throw new Error("company_id is required to read users");
  return onSnapshot(
    query(collection(db, "users"), where("company_id", "==", company)),
    (snap) => onUsers(snap.docs.map((d) => ({ ...d.data(), docId: d.id }))),
    onError
  );
};

/**
 * Waits (max ~6 s) until the signed-in user's token carries this company_id —
 * the same wait as CompanyContext (D-303). On a hard refresh the claim can lag
 * the page; subscribing before it would fail with permission-denied.
 * @returns {Promise<boolean>} true when the claim is there
 */
export const waitForCompanyClaim = async (companyId) => {
  const { getAuth } = await import("firebase/auth");
  const auth = getAuth();
  if (typeof auth.authStateReady === "function") await auth.authStateReady();
  if (!auth.currentUser) return false;
  for (let i = 0; i < 5; i++) {
    const tok = await auth.currentUser.getIdTokenResult(i > 0);
    if (tok.claims.company_id === companyId) return true;
    await new Promise((res) => setTimeout(res, 1500));
  }
  return false;
};
